import React from "react";
import * as MUI from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getStockStatus, formatCurrency } from "../utils/stockStatus";
import { getMeasuringUnitLabel } from "../utils/measuringUnits";
import DetailField from "../../../../../../components/display/DetailField";
import SectionCard from "../../../../../../components/display/SectionCard";

function FlagChip({ label, active, testId }) {
  return (
    <MUI.Chip
      icon={active ? <CheckCircleOutlineIcon /> : <HighlightOffOutlinedIcon />}
      label={label}
      size="small"
      color={active ? "success" : "error"}
      data-testid={testId}
    />
  );
}

/** Full info display for a warehouse item; shared by the popup dialog and the full page. */
export default function WarehouseItemDetailsContent({ item, currency }) {
  const { t } = useTranslation();
  const status = item ? getStockStatus(item.quantity) : null;

  return (
    <MUI.Stack spacing={2.5}>
      {status && status.key !== "ok" && (
        <MUI.Alert
          severity={status.color}
          sx={{ borderRadius: 2.5 }}
          data-testid="warehouse.items.details-dialog.stock-alert"
        >
          {t(status.label)}
        </MUI.Alert>
      )}

      <SectionCard label={t("Details")}>
        <MUI.Stack spacing={2}>
          <DetailField
            label={t("Description")}
            value={item?.description || "-"}
            testId="warehouse.items.details-dialog.field-description"
          />
          <MUI.Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              rowGap: 2,
              columnGap: 2,
            }}
          >
            <DetailField
              label={t("Supplier")}
              value={item?.supplier?.name}
              testId="warehouse.items.details-dialog.field-supplier"
            />
            <DetailField
              label={t("Category")}
              value={item?.category?.name}
              testId="warehouse.items.details-dialog.field-category"
            />
          </MUI.Box>
        </MUI.Stack>
      </SectionCard>

      <SectionCard label={t("InventoryDetails")}>
        <MUI.Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            rowGap: 2,
            columnGap: 2,
          }}
        >
          <DetailField
            label={t("Code")}
            value={item?.code || "-"}
            testId="warehouse.items.details-dialog.field-code"
          />
          <DetailField
            label={t("SerialNumber")}
            value={item?.serialNumber || "-"}
            testId="warehouse.items.details-dialog.field-serial-number"
          />
          <DetailField
            label={t("MeasuringUnit")}
            value={getMeasuringUnitLabel(item?.measuringUnit, t) || "-"}
            testId="warehouse.items.details-dialog.field-measuring-unit"
          />
          <DetailField
            label={t("ExpirationDate")}
            value={
              item?.expirationDate
                ? dayjs(item.expirationDate).format("DD/MM/YYYY")
                : "-"
            }
            testId="warehouse.items.details-dialog.field-expiration-date"
          />
        </MUI.Box>
      </SectionCard>

      <SectionCard label={t("PricingAndStock")}>
        <MUI.Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
            rowGap: 2.5,
            columnGap: 2,
          }}
        >
          <DetailField
            label={t("Price")}
            value={item ? formatCurrency(item.price, currency) : ""}
            testId="warehouse.items.details-dialog.field-price"
          />
          <DetailField
            label={t("PDV")}
            value={`${Number(item?.pdv)}%`}
            testId="warehouse.items.details-dialog.field-pdv"
          />
          <DetailField
            label={t("Discount")}
            value={`${Number(item?.discount)}%`}
            testId="warehouse.items.details-dialog.field-discount"
          />
          <DetailField
            label={t("DiscountedPrice")}
            value={`${formatCurrency(item?.discountedPrice, currency)}`}
            emphasize
            testId="warehouse.items.details-dialog.field-discounted-price"
          />
          <DetailField
            label={t("PriceWithVat")}
            value={item ? formatCurrency(item.priceWithTax, currency) : ""}
            emphasize
            testId="warehouse.items.details-dialog.field-price-with-vat"
          />
          <DetailField
            label={t("DiscountedPriceWithTax")}
            value={
              item ? formatCurrency(item.discountedPriceWithTax, currency) : ""
            }
            emphasize
            testId="warehouse.items.details-dialog.field-discounted-price-with-vat"
          />
          <DetailField
            label={t("Quantity")}
            value={item?.quantity}
            testId="warehouse.items.details-dialog.field-quantity"
          />
        </MUI.Box>
      </SectionCard>

      <SectionCard label={t("AdditionalInformation")}>
        <MUI.Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <FlagChip
            label={t("Sellable")}
            active={Boolean(item?.sellable)}
            testId="warehouse.items.details-dialog.flag-sellable"
          />
          <FlagChip
            label={t("AntiRabiesVaccine")}
            active={Boolean(item?.antiRabiesVaccine)}
            testId="warehouse.items.details-dialog.flag-antiRabiesVaccine"
          />
          <FlagChip
            label={t("OtherVaccine")}
            active={Boolean(item?.otherVaccine)}
            testId="warehouse.items.details-dialog.flag-otherVaccine"
          />
          <FlagChip
            label={t("NarcoticDrug")}
            active={Boolean(item?.narcoticDrug)}
            testId="warehouse.items.details-dialog.flag-narcoticDrug"
          />
          <FlagChip
            label={t("PrescriptionRequired")}
            active={Boolean(item?.prescriptionRequired)}
            testId="warehouse.items.details-dialog.flag-prescriptionRequired"
          />
        </MUI.Stack>
      </SectionCard>
    </MUI.Stack>
  );
}
